import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { isEmpty } from "lodash";

import type { IAddToDashboardConfig } from "app/components/React/WidgetContainer/types";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { getChartConfig } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { DataFormat } from "../../../../components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import {
    dataModeMap,
    numbersDataFormat,
    percentDataFormat,
} from "../../../../components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { useDataFormat } from "../../../../components/ChannelsOverviewBarChart/ChannelsOverviewBarChartSwitcher";
import {
    EViewBySwitcherItems,
    VIEW_BY_TEXT,
} from "../../../../components/ChannelsOverviewBarChart/components/view-by-switcher/ViewBySwitcher";
import { dataFormatToIndex, indexToDataFormat } from "../../../consts";
import type { AddToDashboardButtonProps, IChannelsBetaData } from "../../../types/types";
import { buildSeries } from "../utils";

const useViewBySwitcher = (isCompare: boolean): [number, (val: number) => void] => {
    const param = proNavigator.getParams().channelsOverviewViewByType;
    const defaultView =
        isCompare && param === EViewBySwitcherItems.Domain
            ? EViewBySwitcherItems.Domain
            : EViewBySwitcherItems.Channels;

    return useState<number>(defaultView);
};

export const useBetaChannelsOverviewConfig = (
    data: IChannelsBetaData | null,
    isCompare: boolean,
    addToDashboardButtonProps: AddToDashboardButtonProps,
) => {
    const { params } = useCurrentStateAndParams();
    const { webSource, key, isWWW, duration } = params;

    const is28d = duration === "28d";
    const includeSubDomains = isWWW === "*";
    const timeGranularity = is28d ? "Daily" : "Monthly";

    const domains = useMemo(() => key.split(","), [key]);
    const [viewBy, setViewBy] = useViewBySwitcher(isCompare);
    const [dataFormat, setDataFormat] = useDataFormat(isCompare);

    const series = useMemo(() => {
        if (!data || isEmpty(data)) {
            return [];
        }
        return buildSeries(data, domains, dataFormat, isCompare);
    }, [data, dataFormat, isCompare, domains]);

    const config = useMemo(() => {
        if (!data) {
            return null;
        }

        return getChartConfig({
            data,
            dataFormat,
            isCompare,
            isBenchmarkMode: false,
            showWatermark: false,
        });
    }, [data, dataFormat, domains, isCompare, webSource]);

    const totalVisits = useMemo(() => {
        return data?.[key]?.totalVisits ?? null;
    }, [data, key]);

    const selectedVisualizationIndex = useMemo(() => dataFormatToIndex[dataFormat], [dataFormat]);

    const handleDataFormatChange = (index: number) => {
        const format: DataFormat = indexToDataFormat[index];
        setDataFormat(format);
        allTrackers.dispatchTrackEvent(
            "Measure Button",
            "click",
            `Bar Chart/Traffic Sources Overview/${dataModeMap[format]}`,
        );
        proNavigator.applyUpdateParams({ channelsOverviewDataFormatType: format }, { reload: false });
    };
    const handleViewByChange = (val: number) => {
        setViewBy(val);
        const viewByLabel = EViewBySwitcherItems[val];
        allTrackers.dispatchTrackEvent("toggle", "click", `view by/${viewByLabel}`);
        proNavigator.applyUpdateParams({ channelsOverviewViewByType: viewByLabel }, { reload: false });
    };
    const addToDashboardConfig: IAddToDashboardConfig = {
        metric: "TrafficSourcesOverview",
        type: "BarChartMMXDashboard",
        webSource,
        modelType: "fromWebsite",
        filters: { includeSubDomains, timeGranularity },
        overrideAddToDashboardParams: { dataMode: dataModeMap[dataFormat] },
        buttonProps: addToDashboardButtonProps,
    };
    const switcherByChannelOrDomainItems = Object.values(EViewBySwitcherItems)
        .filter((val) => typeof val === "number")
        .map((val) => ({
            title: translate(VIEW_BY_TEXT[val]),
            value: val,
        }));
    return {
        addToDashboardConfig,
        series,
        config,
        totalVisits,
        viewBy,
        dataFormat,
        switcherByChannelOrDomainConfig: {
            items: switcherByChannelOrDomainItems,
            onItemClick: handleViewByChange,
            initialSelectedItem: viewBy,
        },
        switcherViewModeConfig: {
            items: [
                { title: percentDataFormat, value: dataModeMap[percentDataFormat] },
                { title: numbersDataFormat, value: dataModeMap[numbersDataFormat] },
            ],
            onItemClick: handleDataFormatChange,
            initialSelectedItem: selectedVisualizationIndex,
        },
    };
};
